type FetchArgs = Parameters<typeof fetch>
type FetchWithTimeoutOpts = NonNullable<FetchArgs[1]> & { timeout: number }

function timeoutSignal(timeout: number) {
  if (!Number.isInteger(timeout)) {
    throw new TypeError('Expected an integer')
  }

  const controller = new AbortController()

  const timeoutId = setTimeout(() => {
    controller.abort()
  }, timeout)

  // Allow Node.js processes to exit early if only the timeout is running
  timeoutId?.unref?.()

  return controller.signal
}

const NON_REASON_PHRASE_CHARS = /[^\t\x20-\x7e\x80-\xff]/g

const fetchWithTimeout = async (
  url: FetchArgs[0],
  opts: FetchWithTimeoutOpts,
) => {
  const { timeout, ...fetchOpts } = opts
  const signal =
    typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(timeout)
      : timeoutSignal(timeout)

  try {
    return await fetch(url, {
      ...fetchOpts,
      signal,
    })
  } catch (err) {
    // Only the abort is a timeout; an offline, DNS or header failure keeps
    // its cause so onError can tell them apart
    const isTimeout =
      err instanceof Error &&
      (err.name === 'TimeoutError' || err.name === 'AbortError')
    if (isTimeout) {
      return new Response(null, { status: 408, statusText: 'Timeout' })
    }
    // statusText takes reason-phrase characters only, or Response throws
    return new Response(null, {
      status: 599,
      statusText: `Network error: ${String(err)}`.replace(
        NON_REASON_PHRASE_CHARS,
        ' ',
      ),
    })
  }
}

type ApiError = {
  message: string
  extensions?: { code?: string; reason?: string }
}

type ApiTokens = {
  refreshToken?: string | null
  accessToken?: string | null
}

type ApiResponseBody = {
  errors?: ApiError[]
  data?: { auth?: Record<string, ApiTokens | null | undefined> }
}

export const apiFetch = async (
  apiUrl: string,
  body: Record<string, unknown>,
  headers: Record<string, string> = {},
): Promise<ApiResponseBody> => {
  // Header names are case-insensitive: the Headers class keeps one Content-Type
  const requestHeaders = new Headers(headers)
  requestHeaders.set('Content-Type', 'application/json')
  const doRequest = async () => {
    const response = await fetchWithTimeout(apiUrl, {
      timeout: 10000,
      method: 'POST',
      credentials: 'include',
      headers: requestHeaders,
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      return { errors: [{ message: response.statusText }] }
    }
    return response.json() as Promise<ApiResponseBody>
  }

  let response!: Awaited<ReturnType<typeof doRequest>>

  for (let attempt = 0; attempt < 3; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    response = await doRequest()
    if (!response.errors) {
      return response
    }
  }

  return response
}
